import {
  Box,
  Button,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import React, { useEffect, useState } from 'react'
import { styles } from '../proTab/styles'
import { getOrderFromStack, setStackSort } from './columnSortUtils'
import { TRANSACTION_SORT_DEFAULT_LABEL } from './performanceSortConfig'

export type TransactionSortField =
  | 'createDate'
  | 'brandName'
  | 'cashBack'
  | 'transactionAmount'
  | 'status'
  | 'source'

export type TransactionColumnSortOrder = 'off' | 'asc' | 'desc'

export type TransactionColumnSort = {
  field: TransactionSortField
  order: TransactionColumnSortOrder
}

export const TRANSACTION_TABLE_SORT_COLUMNS: {
  value: TransactionSortField
  label: string
}[] = [
  { value: 'createDate', label: 'date' },
  { value: 'brandName', label: 'retailer' },
  { value: 'transactionAmount', label: 'transaction amount' },
  { value: 'cashBack', label: 'your cash back' },
  { value: 'status', label: 'status' },
  { value: 'source', label: 'source' },
]

export const DEFAULT_TRANSACTION_SORT_STACK: TransactionSortCriterion[] = []

export type TransactionSortState = {
  sortStack: TransactionSortCriterion[]
}

export type TransactionSortCriterion = {
  field: TransactionSortField
  order: 'asc' | 'desc'
}

export function transactionSortStackForApi(
  sortStack: TransactionSortCriterion[]
): TransactionSortCriterion[] {
  return sortStack
}

export function isDefaultTransactionColumnSorts(
  sortStack: TransactionSortCriterion[]
): boolean {
  if (sortStack.length !== DEFAULT_TRANSACTION_SORT_STACK.length) {
    return false
  }
  return sortStack.every(
    (s, i) =>
      s.field === DEFAULT_TRANSACTION_SORT_STACK[i].field &&
      s.order === DEFAULT_TRANSACTION_SORT_STACK[i].order
  )
}

function orderShort(order: 'asc' | 'desc'): string {
  return order === 'asc' ? '↑' : '↓'
}

export function transactionSortTriggerLabel(
  sort: TransactionSortState
): string {
  const active = transactionSortStackForApi(sort.sortStack)
  if (active.length === 0) {
    return TRANSACTION_SORT_DEFAULT_LABEL
  }
  return active
    .map(
      s =>
        `${TRANSACTION_TABLE_SORT_COLUMNS.find(c => c.value === s.field)
          ?.label ?? s.field} ${orderShort(s.order)}`
    )
    .join(' · ')
}

type TransactionSortMenuProps = {
  sort: TransactionSortState
  onChange: (sortStack: TransactionSortCriterion[]) => void
}

const TransactionSortMenu = ({ sort, onChange }: TransactionSortMenuProps) => {
  const classes = styles()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const [draftStack, setDraftStack] = useState(sort.sortStack)

  useEffect(() => {
    if (!open) {
      setDraftStack(sort.sortStack)
    }
  }, [sort.sortStack, open])

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setDraftStack(sort.sortStack.map(s => ({ ...s })))
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleDone = () => {
    onChange(draftStack.map(s => ({ ...s })))
    setAnchorEl(null)
  }

  const displaySort = open ? { sortStack: draftStack } : sort

  const selectMenuProps = {
    disablePortal: true,
    getContentAnchorEl: null,
    anchorOrigin: { vertical: 'bottom' as const, horizontal: 'left' as const },
    transformOrigin: { vertical: 'top' as const, horizontal: 'left' as const },
  }

  const setColumnOrder = (
    field: TransactionSortField,
    order: TransactionColumnSortOrder
  ) => {
    setDraftStack(prev => setStackSort(prev, field, order))
  }

  return (
    <>
      <Button
        type="button"
        className={classes.sortMenuTrigger}
        onClick={handleOpen}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={open ? 'transaction-sort-menu' : undefined}
        endIcon={<ArrowDropDownIcon />}
      >
        <span className={classes.sortMenuTriggerLabel}>
          {transactionSortTriggerLabel(displaySort)}
        </span>
      </Button>
      <Menu
        id="transaction-sort-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ className: classes.sortMenuPaper }}
        MenuListProps={{ className: classes.sortMenuList }}
      >
        <Box
          className={classes.sortMenuContent}
          onClick={e => e.stopPropagation()}
          onKeyDown={e => e.stopPropagation()}
        >
          <Typography component="p" className={classes.sortMenuHeading}>
            sort by column
          </Typography>
          {TRANSACTION_TABLE_SORT_COLUMNS.map(col => {
            const order = getOrderFromStack(col.value, draftStack)
            return (
              <Box key={col.value} className={classes.sortMenuColumnRow}>
                <Typography
                  component="span"
                  className={classes.sortMenuColumnLabel}
                >
                  {col.label}
                </Typography>
                <TextField
                  className={classes.sortMenuColumnOrder}
                  select
                  value={order}
                  onChange={e =>
                    setColumnOrder(
                      col.value,
                      e.target.value as TransactionColumnSortOrder
                    )
                  }
                  variant="outlined"
                  size="small"
                  SelectProps={{ MenuProps: selectMenuProps }}
                  inputProps={{ 'aria-label': `sort ${col.label}` }}
                >
                  <MenuItem value="off">off</MenuItem>
                  <MenuItem value="asc">ascending</MenuItem>
                  <MenuItem value="desc">descending</MenuItem>
                </TextField>
              </Box>
            )
          })}
          <Button
            type="button"
            className={classes.sortMenuDoneButton}
            onClick={handleDone}
            fullWidth
          >
            <Typography variant="button">done</Typography>
          </Button>
        </Box>
      </Menu>
    </>
  )
}

export default TransactionSortMenu
